<template>
  <div id="content" :class="{ 'bg-white': isWhitePage }">
    <transition name="fade" mode="out-in">
      <div
        v-if="sessionBookingLimitReached"
        class="session-booking-error d-flex flex-column align-items-center justify-content-center bg-white"
      >
        <div class="handover-main session-limit-main">
          <i class="fa-light fa-circle-exclamation"></i>
          <p class="handover-paragraph handover-paragraph-top">
            You have reached your available session limit.
          </p>
          <p class="handover-paragraph">
            Please reach out to your employer for more details.
          </p>
          <b-button variant="continue" @click="exitToHome('session_limit_reached')">
            <span>Return to home</span>
          </b-button>
        </div>
      </div>
      <div
        v-else-if="sessionBookingErrorMessage"
        class="session-booking-error d-flex flex-column align-items-center justify-content-center bg-white"
      >
        <h3 class="dmh-start-title brand-text pb-4 mb-3">Service not available</h3>
        <p class="brand-text pb-5 mb-0 text-center h4">{{ sessionBookingErrorMessage }}</p>
        <b-button
          variant="cta"
          size="lg"
          id="return-to-home-button"
          class="py-4 px-5"
          squared
          @click="exitToHome('service_unavailable')"
        >Return to portal</b-button>
      </div>
      <component v-else @scroll-to="scrollTo" :is="currentContent" v-bind="currentProps"/>
    </transition>
  </div>
</template>

<script>
import { returnHome } from "@/utils/embed/partnerEmbed";
import { isZurichEmbed } from "@/utils/embed/embed";
import { trackEvent } from "@/utils/analytics/analytics";
import { BOOKING_STARTED } from "@/utils/analyticsEvents";
import { extractEapPhoneNumbers } from "@/utils/eapPhoneNumbers/eapPhoneNumbers";
import Home from "../pages/Home.vue";
import Wellbeing from "../pages/Wellbeing.vue";
import Mindfulness from "../pages/Minfulness.vue";
import EapGate from "../pages/EapGate.vue";
import Events from "../pages/Events.vue";
import Event from "../pages/Event.vue";
import WhatsHappening from "../pages/WhatsHappening.vue";
import FAQ from "../pages/FAQ.vue";
import Settings from "../pages/Settings.vue";
import Article from "../pages/Article.vue";
import AllArticles from "../pages/AllArticles.vue";
import Course from "../pages/Course.vue";
import AllCourses from "../pages/AllCourses.vue";
import Fitness from "../pages/Fitness.vue";
import Nutrition from "../pages/Nutrition.vue";
import Recipe from "../pages/Recipe.vue";
import Metrics from "../pages/Metrics.vue";
import Rewards from "../pages/Rewards.vue";
import PulseInsights from "../pages/PulseInsights.vue";
import LegacyInsights from "../pages/Insights.vue";
import PulseQuestions from "../pages/PulseQuestions.vue";
import PulseOutcome from "../pages/PulseOutcome.vue";
import WellnessBenefits from "../pages/WellnessBenefits.vue";
import Store from "../pages/Store.vue";
import OrderConfirmation from "../pages/OrderConfirmation.vue";
import AppDownload from "../pages/AppDownload.vue";
import DigitalClinics from "../pages/DigitalClinics";
import DigitalClinicsBookings from "../pages/DigitalClinicsBookings";
import Specsavers from "../pages/Specsavers";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import TCs from "../pages/TCs";
import Redirect from "../pages/Redirect";
import BeCalm from "../pages/BeCalm";
import BeCalmCourse from "../pages/BeCalmCourse";
import MyFutureWellbeing from "../pages/MyFutureWellbeing";
import StudioOverview from "../pages/StudioOverview";
import WellbeingStudio from "../pages/WellbeingStudio";
import DigitalGym from "../pages/DigitalGym";
import TrainingPlan from "../pages/TrainingPlan";
import LayaWellbeingCourse from "../pages/LayaWellbeingCourse";
import LiveEvent from "../pages/LiveEvent";
import LiveEventTest from "../pages/LiveEventTest";
import WellbeingCourse from "../pages/WellbeingCourse";
import Pulse from "../pages/Pulse";
import SoundSpace from "../pages/SoundSpace";
import Podcast from "../pages/Podcast";
import BookingVideoRoom from "../pages/BookingVideoRoom";
import TotalMentalHealthHome from "../pages/TotalMentalHealthHome";
import TotalMentalHealthStart from "../pages/TotalMentalHealthStart";
import TotalMentalHealthSelectCoach from "../pages/TotalMentalHealthSelectCoach";
import TotalMentalHealthYourImpact from "../pages/TotalMentalHealthYourImpact";
import DigitalClinicsGeneric from "../pages/DigitalClinicsGeneric";
import DigitalClinicsSelectClinician from "../pages/DigitalClinicsSelectClinician";
import Community from "../pages/Community";
import CommunityPost from "../pages/CommunityPost";
import CommunityUser from "../pages/CommunityUser";
import Info from "../pages/Info";
import Faqs from "../pages/Faqs";
import Wallet from "../pages/Wallet";
import Blank from "../pages/Blank";
import CompanyBoard from "../pages/CompanyBoard";
import CompanyBoardPost from "../pages/CompanyBoardPost";
import WomensWellbeingStudio from "@/pages/WomensWellbeingStudio.vue";
import WomensDayLiveEvent from "@/pages/WomensDayLiveEvent.vue";
import GpHome from "../pages/GpHome";
import GpSelect from "../pages/GpSelect";
import OpenEmailBrowser from "../pages/OpenEmailBrowser";
import CourseV2 from "@/pages/CourseV2.vue";
import TrainingPlanV2 from "@/pages/TrainingPlanV2.vue";
import StudioEvent from "@/components/Wellbeing/StudioEvent.vue";
import MyProgress from "@/pages/MyProgress.vue";
import wellbeingStudioV2 from "@/pages/WellbeingStudioV2.vue";
import Customise from "@/components/admin/Customise.vue";
import CustomContent from "@/components/admin/CustomContent.vue";
import UserManagement from "@/components/admin/UserManagement.vue";
import DataAnalytics from "@/components/admin/DataAnalytics.vue";
import MyList from "@/pages/MyList.vue";
import Favourites from "@/pages/Favourites.vue";
import Cmh from "./CMH/Cmh.vue";
import NotificationCentre from "@/pages/NotificationCentre.vue";
import ServicesPage from "../pages/ServicesPage.vue";
import ServiceHandoverPage from "../pages/ServiceHandoverPage.vue";
import ArticlePage from "../pages/ArticlePage.vue";
import SessionBooking from "@journeys/components/SessionBooking.vue";
import PaymentConfirmation from "../pages/PaymentConfirmation.vue";
import TransactionCancelled from "../pages/TransactionCancelled.vue";
import CaraWeb from "../pages/CaraWeb.vue";
import Session from "../pages/Session.vue";


const mappings = {
  home: Home,
  totalMentalHealth: TotalMentalHealthHome,
  totalMentalHealthStart: TotalMentalHealthStart,
  totalMentalHealthSelectCoach: TotalMentalHealthSelectCoach,
  totalMentalHealthYourImpact: TotalMentalHealthYourImpact,
  bookingVideoRoom: BookingVideoRoom,
  iosAppDownload: Home,
  wellbeing: Wellbeing,
  mindfulness: Mindfulness,
  article: Article,
  allArticles: AllArticles,
  course: Course,
  wellbeingCourse: WellbeingCourse,
  layaCourse: LayaWellbeingCourse,
  allCourses: AllCourses,
  eap: EapGate,
  events: Events,
  viewEvent: Event,
  viewService: Event,
  viewEventSeries: Event,
  viewWebinar: Events,
  digitalClinics: DigitalClinics,
  digitalClinicsBookings: DigitalClinicsBookings,
  whatsHappening: WhatsHappening,
  myOrganisation: WhatsHappening,
  fitness: Fitness,
  nutrition: Nutrition,
  recipe: Recipe,
  metrics: Metrics,
  rewards: Rewards,
  // `insights` is resolved in currentContent() from the pulse_insights_v2_enabled
  // feature flag — PulseInsights (V2) when on, LegacyInsights when off.
  insightsCheckIn: PulseQuestions,
  insightsReport: PulseOutcome,
  wellnessBenefits: WellnessBenefits,
  wellbeingBenefits: WellnessBenefits,
  wellnessBenefitsOld: WellnessBenefits,
  faq: FAQ,
  settings: Settings,
  fitbit: Store,
  orderConfirmation: OrderConfirmation,
  download: AppDownload,
  specsavers: Specsavers,
  privacy: PrivacyPolicy,
  TCs: TCs,
  Redirect: Redirect,
  beCalm: BeCalm,
  beCalmCourse: BeCalmCourse,
  myFutureWellbeing: MyFutureWellbeing,
  wellbeingSeries: WellbeingStudio,
  digitalGym: DigitalGym,
  trainingPlan: TrainingPlan,
  virtualEvent: LiveEvent,
  virtualEventTest: LiveEventTest,
  digitalGymArticle: LiveEvent,
  pulse: Pulse,
  soundSpace: SoundSpace,
  podcast: Podcast,
  info: Info,
  community: Community,
  communityPost: CommunityPost,
  communityUser: CommunityUser,
  studioOverview: StudioOverview,
  faqs: Faqs,
  digitalClinicsGeneric: DigitalClinicsGeneric,
  digitalClinicsSelectClinician: DigitalClinicsSelectClinician,
  wallet: Wallet,
  blank: Blank,
  companyBoard: CompanyBoard,
  companyBoardPost: CompanyBoardPost,
  companyBoardCategory: CompanyBoard,
  womensDaySeries: WomensWellbeingStudio,
  womensDayEvent: WomensDayLiveEvent,
  gp: GpHome,
  gpSelect: GpSelect,
  myProgress: MyProgress,
  myList: MyList,
  customise: Customise,
  customContent: CustomContent,
  userManagement: UserManagement,
  dataAnalytics: DataAnalytics,
  challenges: Home,
  favourites: Favourites,
  cmh: Cmh,
  start: Cmh,
  serviceDashboard: Cmh,
  select: Cmh,
  therapyQuestionnaire: Cmh,
  sendSchoolQuestionnaire: Cmh,
  therapyAgreement: Cmh,
  servicesPage: ServicesPage,
  serviceHandoverPage: ServiceHandoverPage,
  articlePage: ArticlePage,
  notificationCentre: NotificationCentre,
  sessionBooking: SessionBooking,
  paymentConfirmation: PaymentConfirmation,
  transactionCancelled: TransactionCancelled,
  cara: CaraWeb,
  session: Session,
};
// the mappings below will overwrite the above mapping where NavigationV2 is being used as the nav
const navigationV2Mappings = {
  virtualEvent: StudioEvent,
  digitalGymArticle: StudioEvent,
  trainingPlan: TrainingPlanV2,
  course: CourseV2,
  beCalmCourse: CourseV2,
  layaCourse: CourseV2,
  wellbeingSeries: wellbeingStudioV2,
  gpSelect: GpSelect,
  OpenEmailBrowser: OpenEmailBrowser
};
import config from "../config/constants";
import axios from "axios";
import { fetchService } from "@/utils/sessionEligibility/sessionEligibility";
export default {
  name: "ContentRouter",
  data() {
    return {
      sessionBookingApiError: null,
      sessionBookingLimitReached: false,
      eapPhoneNumbers: [],
    };
  },
  mounted() {
    // Resolved once per session so the slot picker can quote times in the
    // member's own zone rather than whichever machine they happen to be on.
    if (this.$store.getters.user.authenticated) {
      this.$store.dispatch("loadUserTimezone");
    }
  },
  watch: {
    "$route.query.service_type": {
      immediate: true,
      handler(serviceType) {
        this.sessionBookingApiError = null;
        this.sessionBookingLimitReached = false;
        if (this.$route.name === "sessionBooking" && serviceType) {
          this.checkSessionBookingEligibility(serviceType);
          if (serviceType.includes("dmh")) this.fetchEapPhone();
        }
      },
    },
  },
  updated() {
    setTimeout(function () { window.__vimeoRefresh(); }, 1000);
  },
  methods: {
    exitToHome(reason) {
      returnHome(this.$router, { type: "sl:close", v: 1, reason });
    },
    scrollTo(coordinates) {
      this.$emit("scroll-to", coordinates);
    },
    async checkSessionBookingEligibility(serviceType) {
      try {
        const { sessionCapReached } = await fetchService(this.$store.getters.user_id, serviceType);
        this.sessionBookingLimitReached = sessionCapReached;
      } catch {
        // non-fatal — if the eligibility check fails, let the booking flow proceed
      }
      // Only once the member is let through to the appointment format screen, not when
      // they're shown the session limit message instead.
      if (serviceType.includes("dmh") && !this.sessionBookingLimitReached) {
        trackEvent(BOOKING_STARTED, { service: "dmh", section: "appointment_format" });
      }
    },
    // Empty on failure, so ClinicianSelection shows its default number.
    async fetchEapPhone() {
      try {
        const res = await axios.post(
          config.api_env + "/application/api/u/customisations/get-eap-content",
          { user_id: this.$store.getters.user_id },
          config.options
        );
        this.eapPhoneNumbers = extractEapPhoneNumbers(res.data);
      } catch {
        this.eapPhoneNumbers = [];
      }
    },
  },
  computed: {
    sessionBookingErrorMessage() {
      if (this.$route.name !== "sessionBooking") return null;
      if (this.sessionBookingLimitReached) {
        return "You have reached your available session limit. Please reach out to your employer for more details.";
      }
      if (!this.$route.query?.service_type) {
        return "This booking service is missing or unavailable. Please return to the home page and try again.";
      }
      if (this.sessionBookingApiError) {
        return "We couldn't find this booking service. Please return to the home page and try again.";
      }
      return null;
    },
    navType() {
      return this.$store.getters.navType;
    },
    currentContent() {
      // Insights landing: V2 (Pulse) or legacy, per the admin feature flag. The
      // router guard pre-loads the flag before entering, so it's resolved here;
      // a null (unfetched) flag falls back to legacy, the safe default.
      if (this.$route.name === "insights") {
        return this.$store.getters.pulseInsightsV2Enabled
          ? PulseInsights
          : LegacyInsights;
      }
      if (this.navType === 'versionTwo') {
        if (navigationV2Mappings.hasOwnProperty(this.$route.name)) {
          return navigationV2Mappings[this.$route.name]
        }
      }
      return mappings[this.$route.name];
    },
    isWhitePage() {
      let whitePages = ['digitalClinicsGeneric', 'digitalClinicsSelectClinician', 'wallet', 'soundSpace', 'podcast', 'bookingVideoRoom', 'totalMentalHealthSelectCoach', 'totalMentalHealth', 'companyBoard', 'wellbeingSeries', 'digitalGym', 'beCalm', 'myProgress', 'myList', 'favourites', 'gp', 'gpSelect', 'cmh', 'start', 'serviceDashboard', 'select', 'therapyQuestionnaire', 'sendSchoolQuestionnaire', 'therapyAgreement', 'notificationCentre'];
      if (this.$store.getters.skin === 25) {
        whitePages = whitePages.concat(['home', 'wellbeing', 'nutrition', 'fitness', 'eap', 'rewards', 'customise', 'dataAnalytics', 'favourites'])
      }
      return whitePages.includes(this.$route.name)
    },
    currentProps() {
        if (this.$route.name === "sessionBooking") {
          const userId = this.$store.getters.user_id;
          const post = (path, payload) =>
            axios.post(
              config.api_env + "/application/" + path,
              payload,
              config.options
            );

          // Read CSS custom properties exposed by the active skin's LESS theme
          // (see .dfd() mixin in src/assets/themes/_dfd.less). Returns undefined
          // for any var not set, so the SDK falls back to its own defaults.
          const themeStyle = getComputedStyle(document.body);
          const readVar = (name) => themeStyle.getPropertyValue(name).trim() || undefined;

          return {
            userId,
            serviceType: this.$route.query?.service_type || "",
            apiUrl: config.api_env,
            userTimezone: this.$store.getters.userTimezone,
            eapPhoneNumbers: this.eapPhoneNumbers,

            theme: {
              primary: readVar("--brand-primary"),
              secondary: readVar("--brand-secondary"),
              dark: readVar("--brand-text"),
            },
            fonts: {
              regular: "DMSans",
              bold: "DMSans Bold",
            },

            // required because up-spa services are free-by-default
            serviceData: {
              included_in_your_plan: true,
            },

            apiQuestionnaire: {
              getQuestion: async (payload) => {
                const res = await post("api/u/dmh/get-question", payload);
                if (res?.data && res.data.success === false) {
                  this.sessionBookingApiError = res.data.message || "questionnaire not found";
                }
                return res;
              },

              answerQuestion: (payload) => post("api/u/dmh/answer-question", payload),

              createCallbackCase: (payload) =>
                post("api/u/call-back-service/create-callback-case", payload),

              getReviewQuestions: (payload) => post("api/u/dmh/get-review-questions", payload),

              getClinicianDates: (payload) => post("api/u/dmh/get-other-clinicians-dates", payload),

              bookAppointment: (payload) => post("api/u/dmh/book-appointment", payload),

              getPaymentLink: (payload) => post("api/u/dmh/get-payment-link", payload),

              getPostcodeAddress: (payload) => post("api/u/postcode/get-address", payload),

              getAddressFromCoordinates: (payload) =>
                post("api/u/dmh/get-address-from-coordinates", { user_id: userId, ...payload }),

              getFeatureFlags: (payload) =>
                post("api/u/feature-flag", { user_id: userId, ...payload }),

              uploadFile: (formData) =>
                axios.post(
                  config.api_env + "/application/api/u/file-upload/upload-single",
                  formData
                ),

              deleteFile: (payload) => post("api/u/file-upload/delete-s3-file", payload),

              getFileViewUrl: (encodedData) =>
                `${config.api_env}/application/api/u/file-upload/view-file?data=${encodedData}`
            },

            onNavigate: (path) => this.$router.push(path),

            // Zurich shows Previous on the first question too; there is nothing earlier in
            // the journey, so it returns to the service's handover page.
            onPreviousFromFirstQuestion: isZurichEmbed()
              ? () => this.$router.push(`/services/${this.$route.query.service_type}`)
              : null,

            // End of the booking journey. Embedded, the host owns navigation — hand it
            // the booking-complete event and stop, rather than routing to our own home
            // inside an iframe the host is about to close. Standalone this falls back
            // to router.push("/"), matching the SDK's own default.
            onReturn: () =>
              returnHome(this.$router, {
                type: "sl:booking-complete",
                v: 1,
                service_type: this.$route.query?.service_type || "",
              })
          };
        }

        return {};
      }
  },
};
</script>
<style lang="less">
#content {
  min-height: calc(100vh - 200px);
  display: flex;
  justify-content: center;

  &>div {
    width: 100%;
  }
}

.session-booking-error {
  .dmh-start-title {
    letter-spacing: 0;
  }

  .handover-main {
    max-width: 540px;
    margin: 0 auto;
    border-radius: 16px;
    padding: 24px 28px;
    text-align: center;

    .btn-continue {
      width: fit-content;
      font-family: "DMSans Bold";
      letter-spacing: normal;
      background-color: var(--brand-primary);
      border-radius: 15px;
      color: #ffffff;
      padding: 16px 24px;
      font-weight: 700;
      font-size: 1.6rem !important;
      margin-bottom: 12px;
      letter-spacing: normal !important;
    }
  }

  .session-limit-main {
    max-width: 344px;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .btn {
      width: 100%;
    }
  }

  .fa-circle-exclamation {
    font-size: 8rem;
    color: #FF9600;
    margin-bottom: 24px;
  }

  .handover-paragraph {
    font-family: DMSans, sans-serif;
    font-weight: 400;
    font-size: 1.8rem;
    margin-bottom: 20px;
    color: #122130;
    white-space: break-spaces;

    &.handover-paragraph-top {
      font-family: "Raptor V3 Black" !important;
      font-size: 2.5rem;
    }
  }
}

.fade-enter-active {
  transition-duration: 0.5s;
  transition-property: opacity;
  transition-timing-function: ease-in;
}

.fade-leave-active {
  transition-duration: 0s;
  transition-property: opacity;
  transition-timing-function: ease-in;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 1s ease;
}

.slide-fade-leave-active {
  transition: all 0s ease;
}

.slide-fade-enter

/* .slide-fade-leave-active below version 2.1.8 */
  {
  transform: translateX(10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}
</style>
